import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  StripeProvider,
  CheckoutForm,
  convertLegacyStripeStyle,
  type LegacyStripeStyle,
} from '@anyroad/stripe';
import {
  useCreateBookingRequestPaymentIntent,
  useUpdateBookingRequestPaymentIntent,
} from '@anyroad/api';

interface PaymentProps {
  bookingRequestId: string;
  totalPriceCents: number;
  currency?: string;
  stripeTheme?: LegacyStripeStyle;
  isMobileFirstCheckoutWebhooksEnabled: boolean;
  handlePageTwoSubmit: () => void;
  defaultValues?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null | undefined;
    billingAddress?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  onIntentCreated?: (paymentIntentId?: string) => void;
  onError?: () => void;
  onBillingDetailsChange?: (billingDetails: {
    country?: string;
    postal_code?: string;
  }) => void;
}

export interface PaymentHandle {
  submit: () => void;
}

/**
 * Modern Stripe payment integration using refactored @anyroad/stripe components.
 * Demonstrates pattern from scs/checkout/frontend following mobile_first conventions.
 * Integrates with payment intent API endpoints using react-query hooks.
 *
 * @example Usage from parent component:
 * ```tsx
 * const paymentRef = useRef<PaymentHandle>(null);
 *
 * const handleSubmit = () => {
 *   if (paymentRef.current) {
 *     paymentRef.current.submit(); // This triggers the Stripe form submission
 *   }
 * };
 *
 * return (
 *   <>
 *     <Payment ref={paymentRef} {...props} />
 *     <button onClick={handleSubmit}>Submit Payment</button>
 *   </>
 * );
 * ```
 */
export const Payment = forwardRef<PaymentHandle, PaymentProps>(
  (
    {
      bookingRequestId,
      totalPriceCents,
      currency,
      stripeTheme,
      isMobileFirstCheckoutWebhooksEnabled,
      defaultValues,
      handlePageTwoSubmit,
      onIntentCreated,
      onError,
      onBillingDetailsChange,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const checkoutFormRef = useRef<HTMLFormElement>(null);

    const appearance = useMemo(() => {
      const converted = stripeTheme
        ? convertLegacyStripeStyle(stripeTheme)
        : undefined;
      return converted;
    }, [stripeTheme]);

    useEffect(() => {
      if (stripeTheme?.fonts && stripeTheme.fonts.length > 0) {
        stripeTheme.fonts.forEach((font) => {
          if (font.cssSrc) {
            const existingLink = document.querySelector(
              `link[href="${font.cssSrc}"]`
            );
            if (!existingLink) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = font.cssSrc;
              document.head.appendChild(link);
            }
          }
        });
      }
    }, [stripeTheme]);

    const lastAmountRef = useRef<number | null>(null);

    const {
      mutate: createPaymentIntent,
      data: paymentIntentData,
      isPending: isCreatePending,
      error: createError,
    } = useCreateBookingRequestPaymentIntent();

    const { mutate: updatePaymentIntent, error: updateError } =
      useUpdateBookingRequestPaymentIntent();

    const error = createError || updateError;

    useImperativeHandle(
      ref,
      () => ({
        submit: () => {
          if (checkoutFormRef.current) {
            checkoutFormRef.current.requestSubmit();
          }
        },
      }),
      []
    );

    useEffect(() => {
      if (!bookingRequestId || totalPriceCents <= 0) return;

      if (lastAmountRef.current === null) {
        lastAmountRef.current = totalPriceCents;
        createPaymentIntent({
          bookingRequestId,
          data: {
            amount_cents: totalPriceCents,
            currency: currency || 'usd',
          },
        });
      } else if (
        totalPriceCents !== lastAmountRef.current &&
        paymentIntentData?.payment_intent_id
      ) {
        lastAmountRef.current = totalPriceCents;
        updatePaymentIntent({
          bookingRequestId,
          paymentIntentId: paymentIntentData.payment_intent_id,
          data: {
            amount_cents: totalPriceCents,
            currency: currency || 'usd',
          },
        });
      }
    }, [
      bookingRequestId,
      totalPriceCents,
      currency,
      paymentIntentData?.payment_intent_id,
      createPaymentIntent,
      updatePaymentIntent,
    ]);

    useEffect(() => {
      if (paymentIntentData?.payment_intent_id && onIntentCreated) {
        onIntentCreated(paymentIntentData.payment_intent_id);
      }
    }, [paymentIntentData?.payment_intent_id, onIntentCreated]);

    const localizationStrings = {
      errorMessage: formatMessage({
        defaultMessage: 'An error occurred during payment. Please try again.',
        description: 'Error message displayed when a payment error occurs',
      }),
      processingMessage: formatMessage({
        defaultMessage: 'Processing',
        description: 'Message displayed while payment is being processed',
      }),
      requiresPaymentMethodMessage: formatMessage({
        defaultMessage: 'Please enter your payment details.',
        description: 'Message displayed when payment details are required',
      }),
      submitButtonText: formatMessage({
        defaultMessage: 'Confirm Purchase',
        description: 'Text for the submit button on the payment form',
      }),
      successMessage: formatMessage({
        defaultMessage: 'Payment successful',
        description: 'Message displayed when payment is successful',
      }),
    };

    if (isCreatePending) {
      return (
        <div>
          <FormattedMessage
            defaultMessage="Processing"
            description="Message displayed while payment is being processed"
          />
        </div>
      );
    }

    if (error || !paymentIntentData || !paymentIntentData.client_secret) {
      return (
        <div>
          {error?.message ||
            formatMessage({
              defaultMessage:
                'An error occurred during payment. Please try again.',
              description:
                'Error message displayed when a payment error occurs',
            })}
        </div>
      );
    }

    const conditionalProps = {
      [isMobileFirstCheckoutWebhooksEnabled
        ? 'onBeforeConfirm'
        : 'onIntentConfirm']: handlePageTwoSubmit,
    };

    return (
      <StripeProvider
        appearance={appearance}
        clientSecret={paymentIntentData.client_secret}
        connectedAccountId={paymentIntentData.managed_account_token || null}
        publishableKey={process.env.STRIPE_PUBLISHABLE_KEY || ''}
      >
        <CheckoutForm
          ref={checkoutFormRef}
          dataTestId="mobile-first-payment"
          hideButton
          localizationStrings={localizationStrings}
          defaultValues={{
            name: defaultValues?.name || '',
            email: defaultValues?.email || '',
            address: defaultValues?.billingAddress,
            phone: defaultValues?.phone || '',
          }}
          onError={onError}
          onBillingDetailsChange={onBillingDetailsChange}
          {...conditionalProps}
        />
      </StripeProvider>
    );
  }
);

Payment.displayName = 'Payment';
